import type React from "react";
import {
  StyledGroupTitle,
  StyledGroupTitleWrapper,
  StyledGroupWrapper,
} from "../FieldEditor/style";
import { Icon } from "@smartsuite/react-ui/lib";
import { useEffect, useState } from "react";
import type {
  InputDescription,
  MergeAction,
  MergeField,
} from "@smartsuite/automation-engine-api";
import { CaptionBlock } from "../../../../../components/CaptionBlock";
import type { MergeFieldInterface } from "../../../../../components/MergeField";
import { MergeFieldComponent } from "../../../../../components/MergeField";
import type { InputCandidate } from "../../../../../types/candidate";
import {
  PowerSearchSortBy,
  SortByDropdown,
} from "../../../../../components/SortByDropdown";
import * as R from "remeda";
import { TextColorNames } from "@smartsuite/types";

export const MergeActionsEditor: React.FC<{
  description: InputDescription;
  config: InputCandidate;
  onChange: (input: InputCandidate) => void;
}> = ({ description, onChange, config }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [fields, setFields] = useState<MergeFieldInterface[]>([]);
  const [sortBy, setSortBy] = useState<PowerSearchSortBy>(
    PowerSearchSortBy.FieldName,
  );

  useEffect(() => {
    if (description.editor?.editor.oneofKind === "mergeActions") {
      const newFields = description.editor?.editor.mergeActions.fields
        .map((field) => {
          if (
            config.value?.values[0]?.value?.oneofKind === "mergeFieldActions"
          ) {
            const findConfig =
              config.value?.values[0]?.value?.mergeFieldActions?.fields.find(
                (configField) => configField.fieldSlug === field.fieldSlug,
              );
            if (findConfig) {
              return {
                ...field,
                action: findConfig.action,
              };
            }
          }
          return {
            ...field,
            action: field.defaultAction,
          };
        })
        .filter((field) => R.isTruthy(field));

      setFields(sortFields(newFields, PowerSearchSortBy.FieldName));
    }
  }, [config.value]);

  const onChangeMergeField = (fieldSlug: string, action: MergeAction): void => {
    const newFieldsConfig: MergeField[] = fields.map((field) => {
      let newField: MergeField;
      if (field.fieldSlug === fieldSlug) {
        newField = {
          fieldSlug,
          action,
        };
      } else {
        newField = {
          fieldSlug: field.fieldSlug,
          action: field.action ?? field.defaultAction,
        };
      }
      return newField;
    });

    onChange({
      ...config,
      value: {
        values: [
          {
            value: {
              oneofKind: "mergeFieldActions",
              mergeFieldActions: {
                fields: newFieldsConfig,
              },
            },
          },
        ],
      },
    });
  };

  // making sure on onChange is executed in ui initial render
  useEffect(() => {
    if (fields.length === 0) {
      return;
    }
    const newFieldsConfig: MergeField[] = fields.map((field) => ({
      fieldSlug: field.fieldSlug,
      action: field.action ?? field.defaultAction,
    }));
    onChange({
      ...config,
      value: {
        values: [
          {
            value: {
              oneofKind: "mergeFieldActions",
              mergeFieldActions: {
                fields: newFieldsConfig,
              },
            },
          },
        ],
      },
    });
  }, [fields.length]);

  const onChangeSort = (value: PowerSearchSortBy): void => {
    setSortBy(value);
  };

  return (
    <StyledGroupWrapper>
      <StyledGroupTitle>
        <StyledGroupTitleWrapper
          className="collapsible"
          onClick={() => {
            setCollapsed(!collapsed);
          }}
        >
          {
            <Icon
              name={"arrow-down-small"}
              color={TextColorNames.primary}
              size="XS"
              className={collapsed ? "collapsed" : ""}
            />
          }
          <CaptionBlock description={description} largeCaption />
        </StyledGroupTitleWrapper>
      </StyledGroupTitle>
      {!collapsed && (
        <>
          <SortByDropdown onChangeSort={onChangeSort} />
          <ul style={{ minWidth: 500 }}>
            {sortFields(fields, sortBy).map((field) => {
              return (
                <MergeFieldComponent
                  key={field.fieldSlug}
                  field={field}
                  onChangeMergeField={onChangeMergeField}
                />
              );
            })}
          </ul>
        </>
      )}
    </StyledGroupWrapper>
  );
};

const sortFields = (
  fields: MergeFieldInterface[],
  filterType: PowerSearchSortBy,
): MergeFieldInterface[] => {
  if (filterType === PowerSearchSortBy.FieldName) {
    const newFields = fields.sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
    return [...newFields];
  }
  if (filterType === PowerSearchSortBy.FieldType) {
    const newFields = fields.sort((a, b) => {
      if (a.icon < b.icon) {
        return -1;
      }
      if (a.icon > b.icon) {
        return 1;
      }
      return 0;
    });
    return [...newFields];
  }
  if (filterType === PowerSearchSortBy.FieldAction) {
    const newFields = fields.sort((a, b) => {
      if (a?.action === undefined || b?.action === undefined) {
        return 0;
      }
      if (a?.action < b?.action) {
        return -1;
      }
      if (a.action > b.action) {
        return 1;
      }
      return 0;
    });
    return [...newFields];
  }
  return fields;
};
